const GAPI_SCRIPT = 'https://apis.google.com/js/api.js';

let gapiLoadPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

export function loadGooglePickerApi() {
  if (window.google?.picker) return Promise.resolve();

  if (!gapiLoadPromise) {
    gapiLoadPromise = loadScript(GAPI_SCRIPT).then(
      () =>
        new Promise((resolve, reject) => {
          window.gapi.load('picker', {
            callback: resolve,
            onerror: () => reject(new Error('Failed to load Google Picker'))
          });
        })
    );
  }

  return gapiLoadPromise;
}

export async function fetchPickerConfig(url) {
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin'
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const message = data.error || `Could not load Google Picker (${res.status})`;
    throw new Error(message);
  }

  if (!data.picker_available) {
    throw new Error(data.error || 'Google Picker is not available.');
  }

  return data;
}

const MIME_TO_DOC_TYPE = {
  'application/vnd.google-apps.spreadsheet': 'spreadsheet',
  'application/vnd.google-apps.document': 'document',
  'application/vnd.google-apps.presentation': 'slides',
  'application/vnd.google-apps.folder': 'folder'
};

export function docTypeFromMimeType(mimeType) {
  return MIME_TO_DOC_TYPE[mimeType] || 'spreadsheet';
}

export function driveUrlFromPickerDoc(doc) {
  if (doc.url) return doc.url;

  const id = doc.id;
  if (!id) return '';

  switch (docTypeFromMimeType(doc.mimeType)) {
    case 'document':
      return `https://docs.google.com/document/d/${id}/edit`;
    case 'slides':
      return `https://docs.google.com/presentation/d/${id}/edit`;
    case 'folder':
      return `https://drive.google.com/drive/folders/${id}`;
    default:
      return `https://docs.google.com/spreadsheets/d/${id}/edit`;
  }
}

export function openGoogleDrivePicker({ config, onPick, onCancel, onError, spreadsheetsOnly = false }) {
  const { access_token: oauthToken, api_key: developerKey, app_id: appId } = config;

  if (!oauthToken || !developerKey || !appId) {
    throw new Error('Google Picker is missing OAuth token, API key, or app ID.');
  }

  const builder = new window.google.picker.PickerBuilder()
    .setOAuthToken(oauthToken)
    .setDeveloperKey(developerKey)
    .setAppId(appId)
    .setOrigin(window.location.origin)
    .setTitle('Choose from Google Drive')
    .setCallback((data) => {
      if (data[window.google.picker.Response.ERROR]) {
        const message = data[window.google.picker.Response.ERROR];
        onError?.(message);
        return;
      }

      if (data.action === window.google.picker.Action.PICKED) {
        const doc = data.docs?.[0];
        if (doc) onPick(doc);
      } else if (data.action === window.google.picker.Action.CANCEL) {
        onCancel?.();
      }
    });

  if (spreadsheetsOnly) {
    builder.addView(new window.google.picker.DocsView(window.google.picker.ViewId.SPREADSHEETS));
  } else {
    const sheetsView = new window.google.picker.DocsView(window.google.picker.ViewId.SPREADSHEETS);
    const docsView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCUMENTS);
    const slidesView = new window.google.picker.DocsView(window.google.picker.ViewId.PRESENTATIONS);
    const foldersView = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
      .setSelectFolderEnabled(true)
      .setIncludeFolders(true);

    builder
      .addView(sheetsView)
      .addView(docsView)
      .addView(slidesView)
      .addView(foldersView);
  }

  builder.build().setVisible(true);
}
